import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, ChevronRight, ChevronDown, Edit, FolderOpen, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const TYPE_COLORS = {
  asset: 'bg-blue-50 text-blue-700 border-blue-200',
  liability: 'bg-amber-50 text-amber-700 border-amber-200',
  equity: 'bg-purple-50 text-purple-700 border-purple-200',
  revenue: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  direct_expense: 'bg-rose-50 text-rose-700 border-rose-200',
  indirect_expense: 'bg-orange-50 text-orange-700 border-orange-200',
};

const TYPE_LABELS = {
  asset: 'Asset', liability: 'Liability', equity: 'Equity',
  revenue: 'Revenue', direct_expense: 'Direct Expense', indirect_expense: 'Indirect Expense',
};

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ account_code: '', account_name: '', account_type: 'asset', parent_code: '', description: '', is_group: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.account_type = filterType;
      if (search) params.search = search;
      const res = await accountingAPI.chartOfAccounts(params);
      setAccounts(res.data);
    } catch { toast.error('Failed to load accounts'); }
    finally { setLoading(false); }
  }, [search, filterType]);

  useEffect(() => { load(); }, [load]);

  const buildTree = (accs) => {
    const map = {};
    const roots = [];
    accs.forEach(a => { map[a.account_code] = { ...a, children: [] }; });
    accs.forEach(a => {
      if (a.parent_code && map[a.parent_code]) map[a.parent_code].children.push(map[a.account_code]);
      else roots.push(map[a.account_code]);
    });
    return roots;
  };

  const toggleExpand = (code) => setExpanded(e => ({ ...e, [code]: !e[code] }));

  const openCreate = () => {
    setForm({ account_code: '', account_name: '', account_type: 'asset', parent_code: '', description: '', is_group: false });
    setDialog({ open: true, editing: null });
  };

  const openEdit = (acc) => {
    setForm({ account_code: acc.account_code, account_name: acc.account_name, account_type: acc.account_type, parent_code: acc.parent_code || '', description: acc.description || '', is_group: acc.is_group });
    setDialog({ open: true, editing: acc.account_code });
  };

  const handleSave = async () => {
    if (!form.account_code || !form.account_name) { toast.error('Code and name required'); return; }
    try {
      if (dialog.editing) {
        await accountingAPI.updateAccount(dialog.editing, form);
        toast.success('Account updated');
      } else {
        await accountingAPI.createAccount(form);
        toast.success('Account created');
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const tree = buildTree(accounts);
  const groupAccounts = accounts.filter(a => a.is_group);

  const renderNode = (node, depth = 0) => (
    <div key={node.account_code}>
      <div className={`flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100 ${depth > 0 ? 'bg-slate-50/50' : ''}`}
        style={{ paddingLeft: `${12 + depth * 24}px` }} data-testid={`coa-row-${node.account_code}`}>
        {node.children.length > 0 ? (
          <button onClick={() => toggleExpand(node.account_code)} className="p-0.5" data-testid={`coa-toggle-${node.account_code}`}>
            {expanded[node.account_code] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
        ) : <span className="w-5" />}
        {node.is_group ? <FolderOpen className="w-4 h-4 text-amber-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
        <span className="font-mono text-xs text-slate-500 w-14">{node.account_code}</span>
        <span className={`flex-1 text-sm ${node.is_group ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>{node.account_name}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_COLORS[node.account_type] || 'bg-slate-50 text-slate-500'}`}>
          {TYPE_LABELS[node.account_type] || node.account_type}
        </span>
        <span className="text-[10px] text-slate-400 w-12 text-right">{node.normal_balance === 'debit' ? 'Dr' : 'Cr'}</span>
        <button onClick={() => openEdit(node)} className="p-1.5 hover:bg-slate-200 rounded" data-testid={`coa-edit-${node.account_code}`}>
          <Edit className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>
      {expanded[node.account_code] && node.children.map(c => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div data-testid="chart-of-accounts-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Chart of Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">{accounts.length} accounts | Logistics-optimized structure</p>
        </div>
        <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2" data-testid="coa-add-btn">
          <Plus className="w-4 h-4" /> Add Account
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 bg-white border border-slate-200 rounded-md p-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search accounts..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="form-input pl-9" data-testid="coa-search" />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="form-input w-44" data-testid="coa-type-filter">
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
          <span className="w-5" /><span className="w-5" /><span className="w-14">Code</span><span className="flex-1">Account Name</span><span className="w-24">Type</span><span className="w-12 text-right">Nature</span><span className="w-8" />
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
        ) : search || filterType ? (
          accounts.map(a => renderNode({ ...a, children: [] }, 0))
        ) : (
          tree.map(node => renderNode(node, 0))
        )}
        {!loading && accounts.length === 0 && (
          <p className="text-center text-slate-400 py-12">No accounts found</p>
        )}
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialog.editing ? 'Edit Account' : 'Add New Account'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Account Code</label>
                <input type="text" value={form.account_code} onChange={(e) => setForm(f => ({ ...f, account_code: e.target.value }))}
                  className="form-input font-mono" placeholder="e.g. 11013" disabled={!!dialog.editing} data-testid="coa-form-code" />
              </div>
              <div>
                <label className="form-label">Account Type</label>
                <select value={form.account_type} onChange={(e) => setForm(f => ({ ...f, account_type: e.target.value }))}
                  className="form-input" data-testid="coa-form-type">
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="form-label">Account Name</label>
              <input type="text" value={form.account_name} onChange={(e) => setForm(f => ({ ...f, account_name: e.target.value }))}
                className="form-input" placeholder="e.g. Bank - Yes Bank" data-testid="coa-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Parent Account</label>
                <select value={form.parent_code} onChange={(e) => setForm(f => ({ ...f, parent_code: e.target.value }))}
                  className="form-input" data-testid="coa-form-parent">
                  <option value="">No Parent (Root)</option>
                  {groupAccounts.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.is_group} onChange={(e) => setForm(f => ({ ...f, is_group: e.target.checked }))}
                    className="rounded border-slate-300" data-testid="coa-form-group" />
                  Group Account
                </label>
              </div>
            </div>
            <div>
              <label className="form-label">Description</label>
              <input type="text" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="form-input" placeholder="Optional" data-testid="coa-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="coa-form-save">
              {dialog.editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
